export interface ExclusivityCheckFields {
  artistId: number;
  excludeShowId?: number;
  exclusivityClause?: server.dto.ExclusivityClause;
  showEndDisplay: moment.Moment;
  showStartDisplay: moment.Moment;
  venueLocation?: server.dto.Geo;
  venueId: number;
  appointmentPhase: server.dto.AppointmentPhases;
}

export const getExclusivityCheckProblems = (fields: ExclusivityCheckFields) => {
  const exclusivityCheckProblems: server.dto.ExclusivityCheckProblems[] = [];
  const schedulingCheckProblems: server.dto.ExclusivityCheckProblems[] = [];

  if (!fields.artistId) {
    exclusivityCheckProblems.push("hasNoArtist");
    schedulingCheckProblems.push("hasNoArtist");
  }

  if (!fields.venueId) {
    exclusivityCheckProblems.push("hasNoVenue");
  }
  else if (!fields.venueLocation) {
    exclusivityCheckProblems.push("venueHasNoGeoLocation");
  }

  if (fields.appointmentPhase === "cancelled") {
    exclusivityCheckProblems.push("showIsCancelled");
    schedulingCheckProblems.push("showIsCancelled");
  }

  return { exclusivityCheckProblems, schedulingCheckProblems };
}

export interface ExclusivityCountResult {
  exclusivityConflictErrors: server.dto.ExclusivityCheckProblems[]
  schedulingConflictErrors: server.dto.ExclusivityCheckProblems[]
  exclusivityConflictsCount: number
  scheduleConflictsCount: number
}

export const countExclusivityConflicts = async (fields: ExclusivityCheckFields, includeScheduleConflicts: boolean): Promise<ExclusivityCountResult> => {
  const errors = getExclusivityCheckProblems(fields);

  if (errors.exclusivityCheckProblems.length > 0 && errors.schedulingCheckProblems.length > 0) {
    return {
      exclusivityConflictErrors: errors.exclusivityCheckProblems,
      schedulingConflictErrors: errors.schedulingCheckProblems,
      scheduleConflictsCount: 0,
      exclusivityConflictsCount: 0
    }
  }

  const payload: server.dto.ListConflictsWithShowInstruction = {
    artistId: fields.artistId,
    excludeShowId: fields.excludeShowId || null,
    exclusivityClause: fields.exclusivityClause,
    includeScheduleConflicts,
    showStartDisplay: fields.showStartDisplay.toDate(),
    showEndDisplay: fields.showEndDisplay.toDate(),
    venueLocation: fields.venueLocation || null,
  };

  const result = await window.CoreApi.Show.CountConflicts(payload);

  if (result.data.hasErrors) {
    errors.exclusivityCheckProblems.push("unknown");
    errors.schedulingCheckProblems.push("unknown");
  }

  return {
    exclusivityConflictErrors: errors.exclusivityCheckProblems,
    schedulingConflictErrors: errors.schedulingCheckProblems,
    scheduleConflictsCount: result.data.data?.scheduleConflicts ?? 0,
    exclusivityConflictsCount: result.data.data?.exclusivityConflicts ?? 0
  }
}

export interface ExclusivityCheckResult {
  exclusivityCheckProblems: server.dto.ExclusivityCheckProblems[]
  scheduleConflictProblems: server.dto.ExclusivityCheckProblems[]
  exclusivityConflicts: server.dto.ConflictingShow[]
  scheduleConflicts: server.dto.ConflictingShow[]
}

export const fetchExclusivityConflicts = async (fields: ExclusivityCheckFields, includeScheduleConflicts: boolean, lastResult: ExclusivityCheckResult): Promise<ExclusivityCheckResult> => {
  const isEndBeforeStart = fields.showEndDisplay.isBefore(fields.showStartDisplay);

  // The show is in an invalid state so we cannot even try to check for conflicts
  if (isEndBeforeStart) {
    return lastResult;
  }
  
  const errors = getExclusivityCheckProblems(fields);

  if (errors.exclusivityCheckProblems.length > 0 && errors.schedulingCheckProblems.length > 0) {
    return {
      exclusivityCheckProblems: errors.exclusivityCheckProblems,
      scheduleConflictProblems: errors.schedulingCheckProblems,
      scheduleConflicts: [],
      exclusivityConflicts: []
    }
  }

  const payload: server.dto.ListConflictsWithShowInstruction = {
    artistId: fields.artistId,
    excludeShowId: fields.excludeShowId || null,
    exclusivityClause: fields.exclusivityClause,
    includeScheduleConflicts,
    showStartDisplay: fields.showStartDisplay.toDate(),
    showEndDisplay: fields.showEndDisplay.toDate(),
    venueLocation: fields.venueLocation || null,
  };

  const result = await window.CoreApi.Show.ListConflicts(payload);

  if (result.data.hasErrors) {
    errors.exclusivityCheckProblems.push("unknown");
    errors.schedulingCheckProblems.push("unknown");
  }

  return {
    exclusivityCheckProblems: errors.exclusivityCheckProblems,
    scheduleConflictProblems: errors.schedulingCheckProblems,
    scheduleConflicts: result.data.data?.scheduleConflicts ?? [],
    exclusivityConflicts: result.data.data?.exclusivityConflicts ?? [],
  };
}
